import React, { useCallback, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import { buscarClientes } from '../api/common';

const baseStyles = {
  menu: (base) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const mapClienteToOption = (cliente) => {
  const partes = [cliente.nombre];
  if (cliente.identificacion) partes.push(cliente.identificacion);
  if (cliente.ciudad) partes.push(cliente.ciudad);
  return {
    value: cliente._id,
    label: partes.join(' · '),
    data: cliente,
  };
};

/**
 * Selector reusable de clientes con búsqueda asíncrona.
 * Requiere mínimo 3 caracteres para lanzar búsqueda.
 * onChange recibe la opción completa { value, label, data }.
 */
const ClientSearchSelect = ({
  value = null,
  onChange,
  placeholder = 'Buscar cliente por nombre, NIT o ciudad...',
  isClearable = true,
  menuPortalTarget,
  menuPosition = 'fixed',
  styles = {},
  searchMinChars = 3,
  limit = 20,
  onLoadError,
  noOptionsMessage,
  loadingMessage,
  isDisabled = false,
}) => {
  const selectStyles = useMemo(() => ({ ...baseStyles, ...styles }), [styles]);

  const loadOptions = useCallback(
    async (inputValue) => {
      const term = (inputValue || '').trim();
      if (term.length < searchMinChars) return [];
      try {
        const response = await buscarClientes(term, limit);
        const clientes = response?.data?.data || [];
        return clientes.map(mapClienteToOption);
      } catch (error) {
        if (typeof onLoadError === 'function') onLoadError(error);
        return [];
      }
    },
    [searchMinChars, limit, onLoadError]
  );

  const defaultNoOptionsMessage = ({ inputValue }) => {
    if (!inputValue || inputValue.trim().length < searchMinChars) {
      return `Escriba al menos ${searchMinChars} caracteres...`;
    }
    return 'Sin resultados';
  };

  return (
    <AsyncSelect
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
      styles={selectStyles}
      noOptionsMessage={noOptionsMessage || defaultNoOptionsMessage}
      loadingMessage={loadingMessage || (() => 'Buscando...')}
      isDisabled={isDisabled}
      cacheOptions={false}
      defaultOptions={false}
      loadOptions={loadOptions}
      isSearchable
    />
  );
};

export default ClientSearchSelect;
